/**
 * Аварийная схема площадки БЕЗ движка карты (план 21.09, п.5): контуры зданий,
 * линия эвакуации, укрытия и «вы здесь» — чистая геометрия, свёрнутая в пути
 * SVG. Нужна в двух случаях, и оба честные, а не декоративные:
 *  - тайлы и MapLibre ещё грузятся — линию к выходу человек видит РАНЬШЕ
 *    подложки (в окне героя);
 *  - чанк MapLibre не пришёл вовсе (`engineFailed`) — карта заменяется схемой,
 *    а не пустотой.
 * Модуль чистый (без DOM и сигналов) — рисует компонент ui/emergency-svg.tsx.
 */
import type { LonLat } from '@app/geo'
import type { Lang } from '@app/i18n'
import { localized, type Venue } from '@app/rvf'
import { makeProjector, type SvgBox, svgPath } from '../map/svg-project'
import type { EscapeRoute } from './escape-route'

/** Кадр схемы по умолчанию: квадрат — схема живёт и в окне, и на весь экран. */
export const DEFAULT_SCHEME_BOX: SvgBox = { w: 320, h: 320, pad: 18 }

/**
 * Потолок числа контуров: на большой площадке рисовать тысячи полигонов в DOM
 * дороже, чем они стоят, — схема нужна как ориентир, а не как кадастр.
 * Берём первые — порядок в venue.json стабилен, «прыгать» между кадрами схема
 * не будет.
 */
const MAX_FOOTPRINTS = 300

export interface SchemeShelter {
  readonly id: string
  readonly x: number
  readonly y: number
  readonly label: string
  /** Укрытие, к которому ведёт линия (выбрано тапом или посчитано маршрутом). */
  readonly selected: boolean
}

export interface SvgScheme {
  /** Контуры зданий, пути `M…L…Z`. */
  readonly footprints: readonly string[]
  /** Маршрут по сети; пустая строка — сети нет. */
  readonly route: string
  /** Плечо «по прямой» (пунктир); пустая строка — не нужно. */
  readonly direct: string
  readonly shelters: readonly SchemeShelter[]
  /** Точка человека; null — где он, неизвестно (честно не рисуем). */
  readonly start: { readonly x: number; readonly y: number } | null
  readonly box: SvgBox
}

export interface SchemeInput {
  readonly venue: Venue
  readonly route: EscapeRoute
  /** Выбранное тапом укрытие; null — подсветим то, куда ведёт маршрут. */
  readonly shelterId: string | null
  readonly start: LonLat | null
  readonly lang: Lang
  readonly box?: SvgBox
}

/** Внешнее кольцо полигона юнита; битые пары молча пропускаем — в ЧС лучше
 *  схема с одним кривым контуром меньше, чем исключение вместо схемы. */
function outerRing(unit: Venue['units'][number]): readonly LonLat[] {
  const ring: LonLat[] = []
  for (const [lon, lat] of unit.geometry.coordinates[0] ?? []) {
    if (typeof lon === 'number' && typeof lat === 'number') ring.push([lon, lat])
  }
  return ring
}

/** Все точки, которые должны поместиться в кадр. */
function schemePoints(input: SchemeInput, rings: readonly (readonly LonLat[])[]): LonLat[] {
  const pts: LonLat[] = []
  for (const ring of rings) pts.push(...ring)
  for (const s of input.venue.evacuation?.shelters ?? []) pts.push(s.point)
  pts.push(...(input.route.plan?.polyline ?? []))
  pts.push(...(input.route.direct ?? []))
  if (input.start) pts.push(input.start)
  return pts
}

/**
 * Какое укрытие подсветить: выбранное тапом, иначе то, на чей узел вышел
 * маршрут (пользователь видит цель линии, а не абстрактный набор точек).
 */
function selectedShelterId(input: SchemeInput): string | null {
  if (input.shelterId) return input.shelterId
  const targetNode = input.route.plan?.targetNodeId
  if (!targetNode) return null
  return input.venue.evacuation?.shelters?.find((s) => s.node === targetNode)?.id ?? null
}

/** Модель схемы: геометрия площадки, спроецированная в кадр SVG. */
export function buildSvgScheme(input: SchemeInput): SvgScheme {
  const box = input.box ?? DEFAULT_SCHEME_BOX
  const rings = input.venue.units
    .slice(0, MAX_FOOTPRINTS)
    .map(outerRing)
    .filter((r) => r.length > 2)
  const project = makeProjector(schemePoints(input, rings), box)
  const selected = selectedShelterId(input)
  const defaultLang = input.venue.venue.default_language
  const startXY = input.start ? project(input.start) : null
  return {
    footprints: rings.map((ring) => `${svgPath(ring, project)} Z`),
    route: svgPath(input.route.plan?.polyline ?? [], project),
    direct: svgPath(input.route.direct ?? [], project),
    shelters: (input.venue.evacuation?.shelters ?? []).map((s) => {
      const [x, y] = project(s.point)
      return {
        id: s.id,
        x,
        y,
        label: localized(s.name, input.lang, defaultLang),
        selected: s.id === selected,
      }
    }),
    start: startXY ? { x: startXY[0], y: startXY[1] } : null,
    box,
  }
}
